import { Fragment, ReactNode } from 'react';
import { Link } from './Link';
import { Text } from './Text';

/** Jira-style ticket reference, e.g. `REG-14514`. */
const TICKET = /\b([A-Z][A-Z0-9]+-\d+)\b/g;

/**
 * Inline markdown: links `[text](url)`, bold `**x**`, inline code `` `x` ``,
 * bare URLs, and — when `ticketBaseUrl` is set — bare Jira ticket refs
 * (`REG-1234` → `${ticketBaseUrl}REG-1234`). Everything else is plain text.
 * Deliberately small — the summaries / release notes we render only use a
 * handful of inline forms and we avoid pulling a markdown dependency (which
 * would also break the Backstage-free stories).
 */
const INLINE =
  /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^\s)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|(https?:\/\/[^\s)]+)/g;

/** Linkify bare ticket refs in a plain-text run (no markup left to clobber). */
function renderTickets(text: string, baseUrl: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  TICKET.lastIndex = 0;
  let i = 0;
  // eslint-disable-next-line no-cond-assign
  while ((m = TICKET.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(
      <Link key={`${keyBase}-t${i}`} href={`${baseUrl}${m[1]}`} external>
        {m[1]}
      </Link>,
    );
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function renderInline(text: string, ticketBaseUrl?: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  let i = 0;
  const plain = (s: string, key: string): ReactNode =>
    ticketBaseUrl ? (
      <Fragment key={key}>{renderTickets(s, ticketBaseUrl, key)}</Fragment>
    ) : (
      s
    );
  // eslint-disable-next-line no-cond-assign
  while ((m = INLINE.exec(text))) {
    if (m.index > last) out.push(plain(text.slice(last, m.index), `p${i}`));
    const [, linkText, linkUrl, bold, code, bareUrl] = m;
    if (linkUrl) {
      // Absolute http(s) links open in a new tab; relative `/…` links are
      // in-app routes that should navigate client-side (Link handles this).
      const isExternal = /^https?:\/\//.test(linkUrl);
      out.push(
        <Link key={i} href={linkUrl} external={isExternal}>
          {linkText}
        </Link>,
      );
    } else if (bold) {
      out.push(
        <strong key={i} className="font-semibold text-fg">
          {bold}
        </strong>,
      );
    } else if (code) {
      out.push(
        <code
          key={i}
          className="rounded bg-muted-strong px-1 py-0.5 font-mono text-[0.85em]"
        >
          {code}
        </code>,
      );
    } else if (bareUrl) {
      out.push(
        <Link key={i} href={bareUrl} external>
          {bareUrl}
        </Link>,
      );
    }
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) out.push(plain(text.slice(last), `p${i}`));
  return out;
}

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'para'; text: string };

const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map(c => c.trim());

const isTableSeparator = (line: string): boolean =>
  /^\s*\|?[\s:|-]+\|?\s*$/.test(line) && line.includes('-');

/** Split markdown into headings, bullet lists, tables, and paragraphs. */
export function parseBlocks(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  let para: string[] = [];
  let list: string[] = [];

  const flushPara = () => {
    if (para.length) blocks.push({ type: 'para', text: para.join(' ') });
    para = [];
  };
  const flushList = () => {
    if (list.length) blocks.push({ type: 'list', items: list });
    list = [];
  };

  for (let idx = 0; idx < lines.length; idx += 1) {
    const line = lines[idx].trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);

    if (
      isTableRow &&
      idx + 1 < lines.length &&
      isTableSeparator(lines[idx + 1])
    ) {
      flushPara();
      flushList();
      const header = splitRow(line);
      const rows: string[][] = [];
      idx += 2; // skip header + separator
      while (idx < lines.length && /^\s*\|.*\|\s*$/.test(lines[idx])) {
        rows.push(splitRow(lines[idx]));
        idx += 1;
      }
      idx -= 1; // step back; the for-loop will advance
      blocks.push({ type: 'table', header, rows });
    } else if (heading) {
      flushPara();
      flushList();
      blocks.push({
        type: 'heading',
        level: heading[1].length,
        text: heading[2],
      });
    } else if (bullet) {
      flushPara();
      list.push(bullet[1]);
    } else if (line.trim() === '') {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return blocks;
}

export interface MarkdownProps {
  /** Markdown source. */
  children: string;
  /**
   * When set, bare Jira ticket refs (`REG-1234`) become links to
   * `${ticketBaseUrl}REG-1234` (pass the `…/browse/` URL).
   */
  ticketBaseUrl?: string;
}

/**
 * Render a useful subset of GitHub-flavoured markdown (headings, bullet lists,
 * tables, paragraphs + inline links/bold/code/bare URLs / ticket refs) without
 * an external markdown dependency. Used for scaffolder run + approval summaries
 * and GitHub release notes.
 */
export const Markdown = ({ children, ticketBaseUrl }: MarkdownProps) => {
  const blocks = parseBlocks(children);
  return (
    <div className="flex flex-col gap-2">
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          return (
            <Text
              key={i}
              variant="caption"
              className="font-semibold text-fg uppercase tracking-wide"
            >
              {renderInline(b.text, ticketBaseUrl)}
            </Text>
          );
        }
        if (b.type === 'list') {
          return (
            <ul key={i} className="ml-4 list-disc space-y-1">
              {b.items.map((it, j) => (
                <li key={j}>
                  <Text variant="caption" tone="secondary" as="span">
                    {renderInline(it, ticketBaseUrl)}
                  </Text>
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === 'table') {
          return (
            <div key={i} className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-default">
                    {b.header.map((h, j) => (
                      <th key={j} className="py-1.5 pr-4">
                        <Text
                          variant="caption"
                          tone="tertiary"
                          as="span"
                          className="font-semibold uppercase tracking-wide"
                        >
                          {renderInline(h, ticketBaseUrl)}
                        </Text>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((row, r) => (
                    <tr key={r} className="border-b border-default last:border-0">
                      {row.map((cell, c) => (
                        <td key={c} className="py-1.5 pr-4 align-top">
                          <Text variant="caption" tone="secondary" as="span">
                            {renderInline(cell, ticketBaseUrl)}
                          </Text>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <Text key={i} variant="caption" tone="secondary">
            <Fragment>{renderInline(b.text, ticketBaseUrl)}</Fragment>
          </Text>
        );
      })}
    </div>
  );
};
